<template>
  <div class="app-shell">
    <!-- 移动端访问提示遮罩 -->
    <div v-if="showMobileTip" class="mobile-tip-overlay">
      <div class="mobile-tip-dialog">
        <img class="mobile-tip-logo" src="@/assets/logo.png" alt="logo"/>
        <p class="mobile-tip-title">当前页面暂不支持手机浏览器访问</p>
        <p class="mobile-tip-desc">
          请在电脑浏览器中打开本页面，以获得更好的使用体验。
        </p>
        <button class="mobile-tip-copy-btn" @click="copyCurrentUrl">
          复制当前页面链接
        </button>
      </div>
    </div>

    <HeaderBar
        :user-info="userInfo"
        :is-logged-in="isLoggedIn"
        @show-login="isLoginModalOpen = true"
        @logout="handleLogout"
        @update-user-info="handleUserInfoUpdate"
    />
    <router-view/>
    <div class="web-size-info">
      <a href="https://beian.miit.gov.cn" target="_blank">粤ICP备19161034号-14</a>
      | <span>版权所有&copy;厉川({{now}})</span>
    </div>
    <LoginModal
        v-if="isLoginModalOpen"
        @close="isLoginModalOpen = false"
        @success="handleLoginSuccess"
    />
  </div>
</template>

<script setup>
import HeaderBar from '@/components/HeaderBar.vue';
import LoginModal from '@/components/LoginModal.vue';
import {ref, onMounted, onUnmounted, provide} from 'vue';
import {useRouter} from 'vue-router';
import {checkToken, SUCCESS_CODE, FAILED_CODE, getCvTaskCount} from '@/api/api';
import {onLoginRequired} from '@/utils/loginModalBus';

const isLoginModalOpen = ref(false);
const isLoggedIn = ref(false);
const userInfo = ref(null);
const showMobileTip = ref(false);
let disposeLoginListener = null;
const router = useRouter();
const now = new Date().getFullYear();
// 检查令牌是否有效
const verifyToken = async () => {
  // 先从本地存储和 Cookie 中获取 token
  const token = localStorage.getItem('lk-tk') || getCookie('lk-tk');

  // 如果没有 token，直接设置为未登录状态
  if (!token) {
    isLoggedIn.value = false;
    userInfo.value = null;
    return;
  }

  // 有 token，调用接口验证
  try {
    const res = await checkToken();
    if (res.code === SUCCESS_CODE && res.data) {
      // 令牌有效，保存用户信息
      isLoggedIn.value = true;
      userInfo.value = res.data;
      // 如果响应中有新的令牌，更新它
      if (res.data.token) {
        localStorage.setItem('lk-tk', res.data.token);
        setCookie('lk-tk', res.data.token, 10 / 24);
      }
    } else if (res.code === FAILED_CODE) {
      // 令牌无效，清除本地存储并设置为未登录状态
      isLoggedIn.value = false;
      userInfo.value = null;
      localStorage.removeItem('lk-tk');
      deleteCookie('lk-tk');
    } else {
      // 其他错误情况，也设置为未登录状态
      isLoggedIn.value = false;
      userInfo.value = null;
    }
  } catch (error) {
    console.error('Token verification failed:', error);
    // 网络错误等情况，清除无效 token
    isLoggedIn.value = false;
    userInfo.value = null;
    localStorage.removeItem('lk-tk');
    deleteCookie('lk-tk');
  }
};

// 登录成功处理
const handleLoginSuccess = async (loginRes) => {
  // 登录响应中应该包含 token，保存它
  if (loginRes.data && loginRes.data.token) {
    localStorage.setItem('lk-tk', loginRes.data.token);
    setCookie('lk-tk', loginRes.data.token, 10 / 24);
  }
  // 立即检查令牌以获取完整用户信息
  await verifyToken();

  // 如果验证通过，且当前在欢迎页，则检查 CV 任务数量并按需跳转到列表页
  if (!isLoggedIn.value) return;

  try {
    const currentRoute = router.currentRoute.value;
    if (currentRoute && currentRoute.name === 'Welcome') {
      const taskRes = await getCvTaskCount();
      if (
          taskRes &&
          taskRes.code === SUCCESS_CODE &&
          typeof taskRes.data === 'number' &&
          taskRes.data > 0
      ) {
        router.push('/chat').catch(() => {
        });
      }
      // 配额统计由 HeaderBar 在 isLoggedIn 变化时自动拉取
    }
  } catch (error) {
    console.error('Post-login CV task check failed:', error);
  }
};

// 退出登录处理
const handleLogout = () => {
  // 清除 token 和用户信息
  isLoggedIn.value = false;
  userInfo.value = null;
  localStorage.removeItem('lk-tk');
  deleteCookie('lk-tk');
  // 回到首页并直接展示登录窗口，便于重新登录
  router.push({name: 'Welcome'}).catch(() => {
  });
  isLoginModalOpen.value = true;
};

// 处理用户信息更新
const handleUserInfoUpdate = (updatedInfo) => {
  // 更新本地用户信息
  if (userInfo.value) {
    userInfo.value = {
      ...userInfo.value,
      ...updatedInfo
    };
  }
  // 重新验证 token 以获取最新的用户信息
  verifyToken();
};

// Cookie 工具函数
function getCookie(name) {
  const matches = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return matches ? matches[2] : null;
}

function setCookie(name, value, hours) {
  const expires = new Date(Date.now() + hours * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; domain=localhost; secure`;
}

function deleteCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

// 应用启动时立即检查令牌（每次页面打开/刷新都会执行）
// 这会确保每次用户打开页面时都检查 token 的有效性
onMounted(() => {
  console.log("V1.0.5");
  disposeLoginListener = onLoginRequired(() => {
    if (!isLoginModalOpen.value) {
      isLoginModalOpen.value = true;
    }
  });
  verifyToken();

  // 如果是手机浏览器访问，则显示提示遮罩
  if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
    const ua = navigator.userAgent || '';
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    if (isMobile) {
      showMobileTip.value = true;
    }
  }
});

onUnmounted(() => {
  if (disposeLoginListener) {
    disposeLoginListener();
    disposeLoginListener = null;
  }
});

// 显示登录窗口的方法
const showLoginModal = () => {
  isLoginModalOpen.value = true;
};

// 复制当前页面链接
const copyCurrentUrl = async () => {
  const url = window.location.href;
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(url);
    } else {
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    window.alert('链接已复制，请在电脑浏览器中打开该地址。');
  } catch (error) {
    console.error('复制链接失败:', error);
    window.alert('复制失败，请手动复制浏览器地址栏中的链接。');
  }
};

// 提供用户信息和方法给子组件使用
provide('userInfo', userInfo);
provide('isLoggedIn', isLoggedIn);
provide('showLoginModal', showLoginModal);
</script>

<style>
html,
body,
#app {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  background: #0e0a05;
}

.app-shell {
  min-height: 100vh;
  background: #0e0a05;
}

.mobile-tip-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.86);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
}

.mobile-tip-dialog {
  width: 100%;
  max-width: 360px;
  background: #1f1b16;
  border-radius: 12px;
  padding: 22px 20px 20px;
  text-align: center;
  color: #ffffff;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.6);
}

.mobile-tip-logo {
  width: 64px;
  height: 64px;
  object-fit: contain;
  margin-bottom: 10px;
}

.mobile-tip-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
}

.mobile-tip-desc {
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 20px;
  color: #e0d6c8;
}

.mobile-tip-copy-btn {
  width: 100%;
  border: none;
  outline: none;
  padding: 10px 0;
  border-radius: 999px;
  background: linear-gradient(90deg, #f5c169, #f29b38);
  color: #2b1908;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.mobile-tip-copy-btn:active {
  transform: scale(0.98);
  opacity: 0.9;
}

.web-size-info a {
  color: white;
  text-underline: none;
  text-decoration: none;
}

.web-size-info {
  text-decoration: none;
  text-align: center;
  color: white;
  padding-bottom: 20px;
}
</style>
